/* eslint-disable */
import axios from "axios";
import {
    roleMatchesMenuPermission,
    getUserRoleNamesFromSession,
    userHasMenuPermission,
} from "./menuPermissions";

/**
 * StaffSync uses the monolithic PHP API at .../authentication/index.php (function=...).
 * Do NOT point at Laravel roots (e.g. VUE_APP_BASE_URL_HRMS_DFL) — that causes
 * "Target class [RoleController] does not exist" when opening HR modules.
 */
function normalizeStaffSyncApiBase(raw) {
    if (raw == null || raw === '' || String(raw) === 'undefined') {
        return '';
    }
    let url = String(raw).trim().replace(/\/+$/, '');
    /*if (!url.endsWith('/authentication')) {
        url += '/authentication';
    }*/
    if (!url.endsWith('/')) {
        url += '/';
    }
    return url;
}

function resolveStaffSyncApiBase() {
    const forceRemote = String(process.env.VUE_APP_FORCE_REMOTE_API || '').toLowerCase() === 'true';

    // `npm run serve` should hit your local XAMPP API unless you explicitly opt into remote.
    if (process.env.NODE_ENV === 'development' && !forceRemote) {
        const local = normalizeStaffSyncApiBase(
            process.env.VUE_APP_API_BASE_LOCAL || 'http://localhost/staffsync/authentication/'
        );
        if (local) {
            return local;
        }
    }

    const normalized = normalizeStaffSyncApiBase(process.env.VUE_APP_API_BASE);
    if (normalized) {
        return normalized;
    }
    return 'http://localhost/staffsync/authentication/';
}

export const base = resolveStaffSyncApiBase();

if (typeof console !== 'undefined' && console.info) {
    console.info('[StaffSync] API base:', base);
}

export const county_logo = base + "logos/logo.png";
export const login_bg = base + "logos/bg-auth-overlay.png";
export const paysol = base + "paysol/index.php";
export const authUrl = base + "index.php";

export const uploads = base;

/** Extract a readable message from StaffSync JSON or Laravel error responses. */
export function parseApiErrorMessage(error, fallback = 'Request failed') {
    const data = error?.response?.data;
    if (data && typeof data === 'object' && data.message) {
        return String(data.message);
    }
    if (typeof data === 'string') {
        const laravelMsg = data.match(/Target class \[([^\]]+)\] does not exist/i);
        if (laravelMsg) {
            return `Wrong API server (Laravel route error: ${laravelMsg[0]}). Use the StaffSync authentication API — check VUE_APP_API_BASE in .env.`;
        }
        const titleMatch = data.match(/<title[^>]*>([^<]+)<\/title>/i);
        if (titleMatch && /something went wrong/i.test(titleMatch[1])) {
            return 'Wrong API server returned a Laravel error page. Set VUE_APP_API_BASE to your .../authentication/ URL and restart npm run serve.';
        }
    }
    return error?.message || fallback;
}

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const msg = parseApiErrorMessage(error);
        if (/RoleController|Wrong API server/i.test(msg)) {
            console.error('[StaffSync] API misconfiguration:', msg, 'URL:', error?.config?.url);
        }
        if (error?.response?.data && typeof error.response.data === 'object' && error.response.data.message) {
            error.message = error.response.data.message;
        } else if (msg) {
            error.message = msg;
        }
        return Promise.reject(error);
    }
);



// Toast notification utility
export const toast = {
    success(message) {
        this._showToast(message, 'success');
    },
    error(message) {
        this._showToast(message, 'error');
    },
    warning(message) {
        this._showToast(message, 'warning');
    },
    info(message) {
        this._showToast(message, 'info');
    },
    _showToast(message, type) {
        // Create toast element
        const toast = document.createElement('div');
        toast.className = `toast-notification toast-${type}`;
        toast.style.cssText = `
      position: fixed;
      min-width: 250px;
      margin-bottom: 10px;
      padding: 15px;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      transform: translateY(30px);
      opacity: 0;
      transition: all 0.3s ease;
      z-index: 9999;
      color: white;
    `;

        // Apply color based on type
        switch (type) {
            case 'success':
                toast.style.backgroundColor = '#4caf50';
                break;
            case 'error':
                toast.style.backgroundColor = '#f44336';
                break;
            case 'warning':
                toast.style.backgroundColor = '#ff9800';
                break;
            case 'info':
                toast.style.backgroundColor = '#2196f3';
                break;
        }

        toast.innerHTML = `
      <div style="display: flex; align-items: center;">
        <div style="flex-grow: 1;">${message}</div>
      </div>
    `;

        // Add to DOM
        const container = document.getElementById('toast-container') || this._createContainer();
        container.appendChild(toast);

        // Trigger animation
        setTimeout(() => {
            toast.style.transform = 'translateY(0)';
            toast.style.opacity = '1';
        }, 10);

        // Auto-remove after 3 seconds
        setTimeout(() => {
            toast.style.transform = 'translateY(30px)';
            toast.style.opacity = '0';
            setTimeout(() => {
                if (container.contains(toast)) {
                    container.removeChild(toast);
                }
                // Remove container if empty
                if (container.children.length === 0 && document.body.contains(container)) {
                    document.body.removeChild(container);
                }
            }, 300);
        }, 3000);
    },
    _createContainer() {
        const container = document.createElement('div');
        container.id = 'toast-container';
        container.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      z-index: 9999;
    `;
        document.body.appendChild(container);
        return container;
    }
};

export const execute = (params, url) => {
    const tokenStr = sessionStorage.getItem("tokenSession");
    return axios.post(url, params, { headers: { "Authorization": `Bearer ${tokenStr}` } })
}

export const executeJson = (params, url) => {
    const tokenStr = sessionStorage.getItem("tokenSession");
    return axios.post(url, params, {
        headers: {
            "Authorization": `Bearer ${tokenStr}`,
            "Content-Type": "application/json"
        }
    });
}

export const executeDownload = (params, url) => {
    const tokenStr = sessionStorage.getItem("tokenSession");
    return axios.post(url, params, { headers: { "Authorization": `Bearer ${tokenStr}` }, responseType: 'blob' })
}

export const execute2 = (params, url) => {
    return axios.post(url, params)
}

/** Reload selectedRoles from server (employee position) and refresh navigation */
export const refreshSessionRoles = async () => {
    const data = new FormData();
    data.append('function', 'refreshUserRoles');
    const res = await execute(data, authUrl);
    if (res?.data?.success && Array.isArray(res.data.data?.roles)) {
        sessionStorage.setItem('selectedRoles', JSON.stringify(res.data.data.roles));
        window.dispatchEvent(new Event('staffsync-roles-updated'));
        return res.data.data.roles;
    }
    throw new Error(parseApiErrorMessage({ response: res, message: res?.data?.message }, res?.data?.message || 'Failed to refresh roles'));
};

export const getRight = (accessRole) => {
    const target = String(accessRole || '').trim().replace(/\s+/g, ' ').toUpperCase();

    // Category-based admin fallback used widely in route/menu checks.
    if (target === 'ADMIN') {
        try {
            const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || '{}');
            const cat = String(userInfo.category || '').trim().replace(/\s+/g, ' ').toUpperCase();
            if (cat === 'ADMIN' || cat === 'SUPERADMIN' || cat === 'SUPER ADMIN') return true;
        } catch (_) { }
    }

    if (target === 'SUPER ADMIN') {
        try {
            const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || '{}');
            if (String(userInfo.category || '').toLowerCase() === 'superadmin') return true;
        } catch (_) { }
    }

    const userRoles = getUserRoleNamesFromSession();
    if (userRoles.length === 0) {
        return false;
    }

    return userRoles.some((name) => roleMatchesMenuPermission(name, target));
};

/** Same as getRight but accepts multiple permission strings (OR). */
export const getRightAny = (permissions) => userHasMenuPermission(permissions);

/**
 * Format Kenyan mobile number to 254XXXXXXXXX.
 * Converts 0726642205, +254726642205, 254726642205 to 254726642205.
 * Handles numbers and scientific notation (e.g. 2.54705E+11) so they display as full digits.
 * @param {string|number} value - Raw phone (0726642205, +254726642205, 254726642205, or number)
 * @returns {string} - 254726642205 format, or original if not a Kenyan mobile
 */
export const formatMobileKe = (value) => {
    if (value == null || value === '') return value;
    // Avoid scientific notation: coerce number or "2.54705E+11" to full digit string
    let s;
    if (typeof value === 'number' || (typeof value === 'string' && /e[+-]?\d+/i.test(value))) {
        const n = Number(value);
        if (!Number.isNaN(n)) s = n >= 1e9 ? String(Math.floor(n)) : String(value).trim();
        else s = String(value).trim();
    } else {
        s = String(value).trim();
    }
    const digits = s.replace(/\D/g, '');
    if (digits.length < 9) return s;
    if (digits.startsWith('0') && digits.length >= 10) return '254' + digits.substring(1);
    if (digits.startsWith('254')) return digits.length > 12 ? digits.substring(0, 12) : digits;
    if (digits.length >= 9) return '254' + digits.slice(-9);
    return s;
};

// Kenyan Payroll Module API helpers
export const payrollApi = {
    createConfig(payload) {
        return execute({ function: "createPayrollConfig", ...payload }, authUrl);
    },
    getConfigs() {
        return execute({ function: "getPayrollConfigs" }, authUrl);
    },
    getGroupMembers(payroll_config_id) {
        return execute({ function: "getPayrollGroupMembers", payroll_config_id }, authUrl);
    },
    getEmployeesForGroupAssign(payload) {
        return execute({ function: "getEmployeesForPayrollGroupAssign", ...payload }, authUrl);
    },
    bulkAssignEmployees(payload) {
        return execute({ function: "bulkAssignEmployeesToPayrollConfig", ...payload }, authUrl);
    },
    removeEmployeeFromGroup(payload) {
        return execute({ function: "removeEmployeeFromPayrollConfig", ...payload }, authUrl);
    },
    getPayComponents() {
        return execute({ function: "getPayComponents" }, authUrl);
    },
    getEmployeePayComponents(payload) {
        return execute({ function: "getEmployeePayComponents", ...payload }, authUrl);
    },
    setEmployeePayComponent(payload) {
        return execute({ function: "setEmployeePayComponent", ...payload }, authUrl);
    },
    removeEmployeePayComponent(payload) {
        return execute({ function: "removeEmployeePayComponent", ...payload }, authUrl);
    },
    createSalaryAdjustment(payload) {
        return execute({ function: "createSalaryAdjustment", ...payload }, authUrl);
    },
    cancelSalaryAdjustment(payload) {
        return execute({ function: "cancelSalaryAdjustment", ...payload }, authUrl);
    },
    assignEmployeeToConfig(payload) {
        return execute({ function: "assignEmployeeToPayrollConfig", ...payload }, authUrl);
    },
    setEmployeeBasicSalary(payload) {
        return execute({ function: "setEmployeeBasicSalary", ...payload }, authUrl);
    },
    setEmployeePayComponent(payload) {
        return execute({ function: "setEmployeePayComponent", ...payload }, authUrl);
    },
    getStatutoryRates() {
        return execute({ function: "getStatutoryRatesConfig" }, authUrl);
    },
    upsertStatutoryRate(payload) {
        return execute({ function: "upsertStatutoryRateConfig", ...payload }, authUrl);
    },
    generatePayroll(payload) {
        return execute({ function: "generatePayroll", ...payload }, authUrl);
    },
    getPayrollRuns(payload = {}) {
        return execute({ function: "getPayrollRuns", ...payload }, authUrl);
    },
    getPayrollItems(payroll_run_id) {
        return execute({ function: "getPayrollItems", payroll_run_id }, authUrl);
    },
    approvePayrollRun(payroll_run_id) {
        return execute({ function: "approvePayrollRun", payroll_run_id }, authUrl);
    },
    markPayrollRunPaid(payroll_run_id) {
        return execute({ function: "markPayrollRunPaid", payroll_run_id }, authUrl);
    }
};